import { apiClient } from './apiClient';

export interface ApiStatus {
  isConnected: boolean;
  lastChecked: Date;
  latency?: number;
  error?: string;
  serverInfo?: {
    version?: string;
    status?: string;
  };
}

class ApiStatusService {
  private static instance: ApiStatusService;
  private status: ApiStatus = {
    isConnected: false,
    lastChecked: new Date(),
    error: 'Not checked yet'
  };
  private subscribers: ((status: ApiStatus) => void)[] = [];
  private checkInterval: NodeJS.Timeout | null = null;
  // Polling cadence used by subscribe(). 60s strikes a balance between
  // responsiveness to outages and not piling on traffic against /health.
  // Settable via startPeriodicChecks() for callers that want a custom
  // interval — preserved for back-compat.
  private intervalMs: number = 60000;

  private constructor() {}

  public static getInstance(): ApiStatusService {
    if (!ApiStatusService.instance) {
      ApiStatusService.instance = new ApiStatusService();
    }
    return ApiStatusService.instance;
  }

  public getStatus(): ApiStatus {
    return { ...this.status };
  }

  public subscribe(callback: (status: ApiStatus) => void): () => void {
    this.subscribers.push(callback);
    callback(this.getStatus());

    // Polling is gated by subscriber count: kick off on first subscribe,
    // stop on last unsubscribe. Removes the foot-gun where one component's
    // unmount stops polling for siblings still listening.
    if (this.subscribers.length === 1) {
      this.beginPolling();
    }

    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
      if (this.subscribers.length === 0) {
        this.endPolling();
      }
    };
  }

  private beginPolling(): void {
    if (this.checkInterval) return;
    this.checkApiHealth();
    this.checkInterval = setInterval(() => {
      this.checkApiHealth();
    }, this.intervalMs);
  }

  private endPolling(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.getStatus()));
  }

  public async checkApiHealth(): Promise<ApiStatus> {
    const startTime = Date.now();

    try {
      // Try to hit the health endpoint
      const response = await apiClient.getAxiosInstance().get('/health', {
        timeout: 10000 // 10 second timeout
      });

      const latency = Date.now() - startTime;

      this.status = {
        isConnected: true,
        lastChecked: new Date(),
        latency,
        serverInfo: {
          status: response.data?.status || 'healthy',
          version: response.data?.version
        }
      };
    } catch (error: any) {
      const latency = Date.now() - startTime;

      this.status = {
        isConnected: false,
        lastChecked: new Date(),
        latency,
        error: this.getErrorMessage(error)
      };
    }

    this.notifySubscribers();
    return this.getStatus();
  }

  private getErrorMessage(error: any): string {
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout - API server not responding';
    } else if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
      return 'Cannot connect to API server';
    } else if (error.response) {
      return `API Error: ${error.response.status} ${error.response.statusText}`;
    } else if (error.request) {
      return 'Network error - No response from server';
    } else {
      return error.message || 'Unknown API error';
    }
  }

  // Back-compat shim. Subscribe-driven polling now starts automatically on
  // first subscribe(). startPeriodicChecks() only updates the cadence and
  // restarts the interval if one is already running.
  public startPeriodicChecks(intervalMs: number = 60000): void {
    this.intervalMs = intervalMs;
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      this.beginPolling();
    }
  }

  // Back-compat shim. Polling is now driven by subscriber count; explicit
  // stop is a no-op while subscribers remain so an unmount can't kill
  // polling for siblings still listening. Forces stop only when no
  // subscribers exist.
  public stopPeriodicChecks(): void {
    if (this.subscribers.length === 0) {
      this.endPolling();
    }
  }

  public async testConnection(): Promise<boolean> {
    const status = await this.checkApiHealth();
    return status.isConnected;
  }
}

export default ApiStatusService;
export const apiStatusService = ApiStatusService.getInstance();